import React from 'react';
import Hero from './Hero';
import { Wrench, Layers, ArrowRight, ShoppingCart, HeartHandshake, Car, MapPin } from 'lucide-react';
import DonationSection from './DonationSection';

interface HomeProps {
  onNavigate: (page: string) => void;
  onShop: (formType: 'inquiry' | 'sell') => void;
}

const Home: React.FC<HomeProps> = ({ onNavigate, onShop }) => {
  const majorCities = [
    "Peoria", "Springfield", "Quincy", "Macomb", "Galesburg", "Canton", "Pekin", "Jacksonville", "Lincoln", "Havana"
  ];

  return (
    <div className="animate-fade-in space-y-0">
      <Hero onNavigate={onNavigate} onShop={onShop} />
      
      {/* Serving Central Illinois Banner */}
      <div className="bg-slate-900 py-4 overflow-hidden border-y border-white/5">
        <div className="flex whitespace-nowrap animate-marquee gap-12 items-center will-change-transform">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="flex gap-12 items-center">
              {majorCities.map(city => (
                <span key={city} className="text-stone-400 font-bold uppercase tracking-[0.2em] text-[10px] flex items-center gap-2">
                  <MapPin className="w-3 h-3 text-brand-primary" /> {city}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
      <style>{`
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
        @keyframes marquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
      `}</style>

      {/* Worth the Drive Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 bg-stone-50 rounded-[3rem] p-8 md:p-16 border border-stone-200">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-brand-primary/10 text-amber-950 px-4 py-1 rounded-full mb-6 font-bold text-sm border border-brand-primary/20">
                <Car className="w-4 h-4" /> Worth the Drive
              </div>
              <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-800 mb-6 leading-tight">
                Central Illinois' <span className="text-amber-800 italic">Hidden Gem</span>.
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                We aren't just a local shop—we are a regional destination for collectors and readers. Whether you're coming from <strong>Peoria, Springfield, or Quincy</strong>, we promise a selection and service level that makes every mile worth it.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 mb-8">
                <div>
                   <p className="font-bold text-slate-800 text-xl">1.5 hrs</p>
                   <p className="text-xs text-slate-500 uppercase font-bold tracking-widest">Maximum Radius</p>
                </div>
                <div>
                   <p className="font-bold text-slate-800 text-xl">50+ Cities</p>
                   <p className="text-xs text-slate-500 uppercase font-bold tracking-widest">Served Locally</p>
                </div>
                <div>
                   <p className="font-bold text-slate-800 text-xl">Thousands</p>
                   <p className="text-xs text-slate-500 uppercase font-bold tracking-widest">Of Items in Stock</p>
                </div>
              </div>
              <button 
                onClick={() => onNavigate('visit')}
                className="bg-slate-900 text-white px-8 py-4 rounded-xl font-bold flex items-center gap-2 hover:bg-slate-800 transition-all shadow-lg"
              >
                Plan Your Trip <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100 transform -rotate-1">
                   <p className="font-bold text-slate-800">Macomb / Canton</p>
                   <p className="text-amber-800 font-bold text-sm">~30 Mins Away</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100 transform rotate-2">
                   <p className="font-bold text-slate-800">Peoria / Pekin</p>
                   <p className="text-amber-800 font-bold text-sm">~1.1 Hours Away</p>
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100 transform rotate-1">
                   <p className="font-bold text-slate-800">Springfield</p>
                   <p className="text-amber-800 font-bold text-sm">~1.2 Hours Away</p>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-stone-100 transform -rotate-2">
                   <p className="font-bold text-slate-800">Quincy / Galesburg</p>
                   <p className="text-amber-800 font-bold text-sm">~1.3 Hours Away</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services Quick Links */}
      <section className="bg-brand-paper py-12 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="flex flex-col md:flex-row gap-8 items-center justify-between bg-white p-8 rounded-2xl shadow-sm border border-stone-200">
              <div className="flex-1">
                 <h2 className="text-2xl font-serif font-bold text-slate-800 mb-2">Technical Services & Support</h2>
                 <p className="text-slate-600">From disc resurfacing to console repair and book donations, we're here to keep the stories alive.</p>
              </div>
              <div className="flex flex-wrap gap-4 justify-center">
                 <button 
                  onClick={() => onNavigate('donations')}
                  className="flex items-center gap-2 bg-brand-accent text-white px-6 py-3 rounded-xl font-bold hover:opacity-90 transition-all shadow-lg"
                 >
                    <HeartHandshake className="w-5 h-5" /> Donate Books
                 </button>
                 <button 
                  onClick={() => onNavigate('services')}
                  className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg"
                 >
                    <Wrench className="w-5 h-5 text-brand-primary" /> Repair & Cleaning
                 </button>
                 <button 
                  onClick={() => onNavigate('services')}
                  className="flex items-center gap-2 bg-brand-primary text-brand-dark px-6 py-3 rounded-xl font-bold hover:bg-amber-500 transition-colors shadow-lg"
                 >
                    <Layers className="w-5 h-5" /> Disc Resurfacing
                 </button>
              </div>
           </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-20 bg-stone-50">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
             
             {/* Left: About Blurb */}
             <div className="bg-white border border-stone-100 rounded-3xl p-8 md:p-12 shadow-sm flex flex-col justify-center">
                <h2 className="text-4xl font-serif font-bold text-slate-800 mb-6">More Than Just a Store</h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                  Welcome to Borden's Bookshoppe, your friendly neighborhood Bookstore in Astoria, IL. We specialize in buying, selling, and trading used books, video games, and media. Beyond retail, we offer disc resurfacing and console repair services for the entire Central Illinois region.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button 
                    onClick={() => onNavigate('about')}
                    className="px-8 py-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                  >
                    Read Our Story <ArrowRight className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => onNavigate('gallery')}
                    className="px-8 py-4 border-2 border-slate-200 text-slate-700 font-bold rounded-xl hover:border-brand-primary transition-all text-center"
                  >
                    Gallery
                  </button>
                </div>
             </div>

             {/* Right: We Buy Stuff */}
             <div className="bg-brand-accent rounded-3xl p-8 md:p-12 text-white shadow-xl overflow-hidden relative flex flex-col justify-center">
               <div className="absolute top-0 right-0 -mt-10 -mr-10 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
               
               <div className="relative z-10">
                 <h2 className="text-3xl font-bold font-serif mb-4">We Buy Your Used Stuff!</h2>
                 <p className="text-teal-50 text-lg mb-8">
                   Cleaning out your attic? We offer cash or store credit for your old video games, consoles, books, and movies. Bulk lots welcome!
                 </p>
                 
                 <div className="space-y-6">
                   <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm font-medium">
                     <li className="flex items-center gap-2">✓ No appointment needed</li>
                     <li className="flex items-center gap-2">✓ Testing on-site</li>
                     <li className="flex items-center gap-2">✓ Cash or Store Credit</li>
                     <li className="flex items-center gap-2">✓ Bulk lots welcome</li>
                   </ul>
                   
                   <div className="pt-4">
                      <button 
                        onClick={() => onShop('sell')}
                        className="w-full sm:w-auto bg-white text-brand-accent hover:bg-teal-50 font-bold py-4 px-10 rounded-xl shadow-lg transition-transform hover:scale-105 flex items-center justify-center gap-2"
                      >
                        <ShoppingCart className="w-5 h-5" /> Get a Quote Now
                      </button>
                   </div>
                 </div>
               </div>
             </div>

           </div>
         </div>
      </section>

      <DonationSection />
    </div>
  );
};

export default Home;